import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Store, Send, Copy, Check, Loader2 } from "lucide-react";
import api from "../api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { toast } from "../hooks/use-toast";

export const InviteBusiness = ({ city = "", country = "", variant = "link" }) => {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", city, country, email: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [copied, setCopied] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) return;
    setBusy(true);
    try {
      const { data } = await api.post("/invites", { ...form, city: form.city || city, country: form.country || country });
      setDone(data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast({ title: t("invite.signInTitle"), description: t("invite.signInMsg") });
        setOpen(false);
        navigate("/login");
      } else {
        toast({ title: t("invite.failed"), variant: "destructive" });
      }
    } finally {
      setBusy(false);
    }
  };

  const shareMsg = done ? t("invite.shareMsg", { name: form.name.trim(), url: done.shareUrl }) : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareMsg);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast({ title: t("invite.copy"), description: shareMsg });
    }
  };

  const reset = (v) => {
    setOpen(v);
    setDone(null);
    setForm({ name: "", city, country, email: "", note: "" }); // re-read the map's current area each time
  };

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogTrigger asChild>
        <button data-testid="invite-business-trigger"
          className={variant === "button"
            ? "inline-flex items-center gap-2 bg-[#0f2a1a] hover:bg-[#16341f] text-white text-sm font-bold px-4 py-2.5 rounded-xl transition-colors"
            : "inline-flex items-center gap-1.5 text-xs font-bold text-[#0e7a3c] dark:text-[#7ee0a3] underline decoration-dotted underline-offset-4 hover:decoration-solid"}>
          <Store size={variant === "button" ? 15 : 13} /> {t("invite.cta")}
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="invite-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold">{t("invite.title")}</DialogTitle>
          <DialogDescription className="text-sm">{t("invite.subtitle")}</DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="space-y-4" data-testid="invite-success">
            <div className="rounded-xl bg-[#e7f6ec] dark:bg-[#16341f] border border-[#16a34a]/30 p-4">
              <p className="text-sm font-bold text-[#0e7a3c] dark:text-[#7ee0a3] flex items-center gap-2">
                <Check size={16} /> {done.emailed ? t("invite.sentEmail") : t("invite.logged")}
              </p>
              <p className="text-xs text-muted-foreground mt-1">{t("invite.nudge")}</p>
            </div>
            <p className="text-xs bg-secondary rounded-xl p-3 text-muted-foreground break-words">{shareMsg}</p>
            <div className="flex gap-2">
              <a href={`https://wa.me/?text=${encodeURIComponent(shareMsg)}`} target="_blank" rel="noopener noreferrer"
                data-testid="invite-whatsapp"
                className="flex-1 text-center bg-[#25D366] hover:brightness-95 text-white text-sm font-bold px-4 py-2.5 rounded-xl transition-all">
                {t("invite.whatsapp")}
              </a>
              <Button type="button" variant="outline" onClick={copy} data-testid="invite-copy" className="font-bold">
                {copied ? <Check size={15} /> : <Copy size={15} />}
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <Input value={form.name} onChange={set("name")} placeholder={t("invite.namePh")} data-testid="invite-name" required maxLength={120} />
            <div className="grid grid-cols-2 gap-2">
              <Input value={form.city} onChange={set("city")} placeholder={t("invite.cityPh")} data-testid="invite-city" maxLength={80} />
              <Input value={form.country} onChange={set("country")} placeholder={t("invite.countryPh")} data-testid="invite-country" maxLength={80} />
            </div>
            <Input type="email" value={form.email} onChange={set("email")} placeholder={t("invite.emailPh")} data-testid="invite-email" maxLength={160} />
            <Textarea value={form.note} onChange={set("note")} placeholder={t("invite.notePh")} data-testid="invite-note" rows={3} maxLength={400} />
            <Button type="submit" disabled={busy || form.name.trim().length < 2} data-testid="invite-submit"
              className="w-full bg-[#16a34a] hover:bg-[#0e7a3c] text-white font-bold">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <><Send size={15} className="mr-2" /> {t("invite.submit")}</>}
            </Button>
            <p className="text-[11px] text-muted-foreground text-center">{t("invite.privacy")}</p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InviteBusiness;
